import React, { useEffect, useState, useRef, useCallback, useMemo, useImperativeHandle } from 'react';
import {
  Plus,
  Minus,
  RefreshCw,
  Maximize2,
  Compass,
  Info,
  MapPin,
  Flame,
  CloudSun,
  Plane,
  Wifi,
  Coins,
  Users,
  Target
} from 'lucide-react';
import { fetchLiveFlights, JarvisFlight } from '@/services/jarvisFlightService';

export interface JarvisGlobeRef {
  zoomIn: () => void;
  zoomOut: () => void;
  resetView: () => void;
  focusCoordinates: (lat: number, lng: number, nextZoom?: number) => void;
}

interface JarvisGlobeProps {
  filterMode?: 'crises' | 'weather' | 'population' | 'flights' | 'network' | 'gdp' | 'heatmap';
  selectedCountryName?: string | null;
  onCountrySelect?: (country: any) => void;
  onCrisisSelect?: (crisis: any) => void;
  onGlobeInteract?: () => void;
  autoRotate?: boolean;
  showAtmosphere?: boolean;
  userLocation?: { lat: number; lng: number } | null;
  onStateSelect?: (stateData: any) => void;
  onStatesLoaded?: (states: any[]) => void;
  showStateNames?: boolean;
}

// Major flight hubs
const FLIGHT_HUBS = [
  { name: 'JFK (New York)', lat: 40.64, lng: -73.78, planes: 142, code: 'JFK' },
  { name: 'LHR (London)', lat: 51.47, lng: -0.46, planes: 189, code: 'LHR' },
  { name: 'DXB (Dubai)', lat: 25.25, lng: 55.36, planes: 210, code: 'DXB' },
  { name: 'BOM (Mumbai)', lat: 19.09, lng: 72.87, planes: 95, code: 'BOM' },
  { name: 'HND (Tokyo)', lat: 35.55, lng: 139.78, planes: 167, code: 'HND' },
  { name: 'LAX (Los Angeles)', lat: 33.94, lng: -118.41, planes: 154, code: 'LAX' },
  { name: 'SIN (Singapore)', lat: 1.36, lng: 103.99, planes: 120, code: 'SIN' },
  { name: 'SYD (Sydney)', lat: -33.95, lng: 151.18, planes: 74, code: 'SYD' },
  { name: 'CDG (Paris)', lat: 49.01, lng: 2.55, planes: 115, code: 'CDG' },
  { name: 'PEK (Beijing)', lat: 40.08, lng: 116.58, planes: 130, code: 'PEK' },
];

// Curated active conflicts (Wars)
const ACTIVE_WARS = [
  { id: "war-ukraine", title: "Russo-Ukrainian War", wikiTitle: "Russo-Ukrainian War", lat: 48.3794, lng: 31.1656, country: "Ukraine", intensity: "High State Conflict", type: "war", size: 8, color: "#ef4444" },
  { id: "war-gaza", title: "Israel–Hamas War", wikiTitle: "Israel–Hamas War", lat: 31.3547, lng: 34.3088, country: "Palestine / Israel", intensity: "High State Conflict", type: "war", size: 8, color: "#ef4444" },
  { id: "war-sudan", title: "Sudanese Civil War", wikiTitle: "Sudanese Civil War (2023–present)", lat: 15.5007, lng: 32.5599, country: "Sudan", intensity: "High Civil War", type: "war", size: 8, color: "#ef4444" },
  { id: "war-yemen", title: "Yemeni Civil War", wikiTitle: "Yemeni civil war (2014–present)", lat: 15.3694, lng: 44.1910, country: "Yemen", intensity: "Regionalized Conflict", type: "war", size: 7, color: "#ef4444" },
  { id: "war-syria", title: "Syrian Civil War", wikiTitle: "Syrian civil war", lat: 34.8021, lng: 38.9968, country: "Syria", intensity: "Multi-party Conflict", type: "war", size: 7, color: "#ef4444" },
  { id: "war-myanmar", title: "Myanmar Civil War", wikiTitle: "Myanmar civil war (2021–present)", lat: 21.9162, lng: 95.9560, country: "Myanmar", intensity: "Intense Civil Insurgency", type: "war", size: 7, color: "#ef4444" },
  { id: "war-drc", title: "Kivu Conflict", wikiTitle: "Kivu conflict", lat: -1.6706, lng: 29.2285, country: "DR Congo", intensity: "Ongoing Armed Insurgency", type: "war", size: 7, color: "#ef4444" },
  { id: "war-somalia", title: "Somali Civil War", wikiTitle: "Somali civil war (2009–present)", lat: 5.1521, lng: 46.1996, country: "Somalia", intensity: "Insurgency", type: "war", size: 6, color: "#ef4444" },
  { id: "war-sahel", title: "Sahel Insurgency", wikiTitle: "Insurgency in the Sahel", lat: 14.5000, lng: -1.5000, country: "Mali / Niger", intensity: "Jihadist Insurgency", type: "war", size: 7, color: "#ef4444" }
];

// Major global city coordinates
const WEATHER_CITIES = [
  { name: "New York", lat: 40.7128, lng: -74.0060, country: "United States" },
  { name: "London", lat: 51.5074, lng: -0.1278, country: "United Kingdom" },
  { name: "Tokyo", lat: 35.6762, lng: 139.6503, country: "Japan" },
  { name: "Paris", lat: 48.8566, lng: 2.3522, country: "France" },
  { name: "Mumbai", lat: 19.0760, lng: 72.8777, country: "India" },
  { name: "Sydney", lat: -33.8688, lng: 151.2093, country: "Australia" },
  { name: "Cairo", lat: 30.0444, lng: 31.2357, country: "Egypt" },
  { name: "Moscow", lat: 55.7558, lng: 37.6173, country: "Russia" },
  { name: "São Paulo", lat: -23.5505, lng: -46.6333, country: "Brazil" },
  { name: "Johannesburg", lat: -26.2041, lng: 28.0473, country: "South Africa" },
  { name: "Singapore", lat: 1.3521, lng: 103.8198, country: "Singapore" },
  { name: "Dubai", lat: 25.2048, lng: 55.2708, country: "UAE" },
  { name: "Lagos", lat: 6.5244, lng: 3.3792, country: "Nigeria" },
  { name: "Reykjavik", lat: 64.1466, lng: -21.9426, country: "Iceland" },
  { name: "Nuuk", lat: 64.1743, lng: -51.7373, country: "Greenland" },
  { name: "Buenos Aires", lat: -34.6037, lng: -58.3816, country: "Argentina" },
  { name: "Honolulu", lat: 21.3069, lng: -157.8583, country: "United States" },
  { name: "Jakarta", lat: -6.2088, lng: 106.8456, country: "Indonesia" },
  { name: "Stockholm", lat: 59.3293, lng: 18.0686, country: "Sweden" },
  { name: "Nairobi", lat: -1.2921, lng: 36.8219, country: "Kenya" },
  { name: "Shanghai", lat: 31.2304, lng: 121.4737, country: "China" },
  { name: "Los Angeles", lat: 34.0522, lng: -118.2437, country: "United States" },
  { name: "Anchorage", lat: 61.2181, lng: -149.9003, country: "United States" }
];

const CONTINENT_LABELS = [
  { name: "North America", lat: 38, lng: -97 },
  { name: "South America", lat: -15, lng: -60 },
  { name: "Europe", lat: 48, lng: 18 },
  { name: "Africa", lat: 2, lng: 16 },
  { name: "Asia", lat: 44, lng: 95 },
  { name: "Oceania", lat: -25, lng: 135 },
  { name: "Antarctica", lat: -78, lng: 0 }
];

export const JarvisGlobe = React.memo(React.forwardRef<JarvisGlobeRef, JarvisGlobeProps>(({
  filterMode = 'weather',
  selectedCountryName,
  onCountrySelect,
  onCrisisSelect,
  onGlobeInteract,
  autoRotate = false,
  showAtmosphere = true,
  userLocation = null,
  onStateSelect,
  onStatesLoaded,
  showStateNames = true
}, ref) => {
  const mapRef = useRef<SVGSVGElement>(null);
  const mapGroupRef = useRef<SVGGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // High-performance Flat Map Pan and Zoom Refs (Bypasses React re-renders)
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(1.0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const draggedRef = useRef(false);
  const mouseStartPos = useRef({ x: 0, y: 0 });

  // Map Data 
  const [rawCountries, setRawCountries] = useState<any[]>([]);
  const [geoJsonData, setGeoJsonData] = useState<any[]>([]);
  const [naturalCrisisPoints, setNaturalCrisisPoints] = useState<any[]>([]);
  const [weatherPoints, setWeatherPoints] = useState<any[]>([]);
  const [flightPoints, setFlightPoints] = useState<JarvisFlight[]>([]);
  const [stateGeoJson, setStateGeoJson] = useState<any | null>(null);
  const [loadingStates, setLoadingStates] = useState(false);

  // Load state borders when country is selected
  useEffect(() => {
    if (!selectedCountryName || rawCountries.length === 0) {
      setStateGeoJson(null);
      return;
    }
    const country = rawCountries.find(c => c.name === selectedCountryName);
    if (!country || !country.cca3) {
      setStateGeoJson(null);
      return;
    }

    setLoadingStates(true);

    fetch(`https://www.geoboundaries.org/api/current/gbOpen/${country.cca3}/ADM1/`)
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('API failed');
      })
      .then(data => {
        // Prefer simplified geometry for faster loading and better rendering performance
        const rawGjUrl = data.simplifiedGeometryGeoJSON || data.gjDownloadURL;
        let gjUrl = rawGjUrl;
        if (gjUrl) {
          if (gjUrl.includes('github.com') && gjUrl.includes('/raw/')) {
            gjUrl = gjUrl.replace('github.com', 'media.githubusercontent.com/media').replace('/raw/', '/');
          } else if (gjUrl.includes('raw.githubusercontent.com')) {
            gjUrl = gjUrl.replace('raw.githubusercontent.com', 'media.githubusercontent.com/media');
          }
        }
        if (gjUrl) {
          const fetchWithProxy = async (url: string) => {
            try {
              // Try direct fetch first (geoboundaries/github usually supports CORS directly and is extremely fast)
              const res = await fetch(url);
              if (!res.ok) throw new Error('Direct fetch failed');
              return await res.json();
            } catch (e) {
              console.warn('Direct fetch failed, trying corsproxy.io:', e);
              try {
                // Try corsproxy.io
                const res = await fetch(`https://corsproxy.io/?${encodeURIComponent(url)}`);
                if (!res.ok) throw new Error('Corsproxy.io failed');
                return await res.json();
              } catch (e2) {
                console.warn('Corsproxy.io proxy failed, trying codetabs:', e2);
                try {
                  const res = await fetch(`https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`);
                  if (!res.ok) throw new Error('Codetabs proxy failed');
                  return await res.json();
                } catch (e3) {
                  throw new Error('All fetch attempts failed');
                }
              }
            }
          };
          return fetchWithProxy(gjUrl);
        }
        throw new Error('No GeoJSON url');
      })
      .then(geojson => {
        setStateGeoJson(geojson);
        setLoadingStates(false);
        if (geojson && geojson.features && onStatesLoaded) {
          const states = geojson.features.map((f: any, idx: number) => {
            const name = f.properties?.shapeName || f.properties?.NAME || f.properties?.name || `State ${idx + 1}`;
            const center = getStateCenter(f);
            return {
              name,
              lat: center?.lat,
              lng: center?.lon,
              country: selectedCountryName
            };
          }).filter((s: any) => s.name);
          onStatesLoaded(states);
        }
      })
      .catch(err => {
        console.error('Failed to load state borders for', country.name, err);
        setStateGeoJson(null);
        setLoadingStates(false);
      });
  }, [selectedCountryName, rawCountries]);

  // Update SVG transform property directly for smooth DOM panning/zooming
  const updateTransform = useCallback(() => {
    if (mapGroupRef.current) {
      mapGroupRef.current.style.transform = `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${zoomRef.current})`;

      // Directly toggle continent labels visibility based on zoom level to maintain 60fps performance
      const continentsGroup = mapGroupRef.current.querySelector('.continents-group') as HTMLElement | null;
      if (continentsGroup) {
        if (zoomRef.current > 2.2) {
          continentsGroup.style.display = 'none';
        } else {
          continentsGroup.style.display = 'inline';
        }
      }
    }
  }, []);

  // Equirectangular Projection helper
  const projectPoint = useCallback((lng: number, lat: number) => {
    const x = (lng + 180) * (1000 / 360);
    const y = (90 - lat) * (500 / 180);
    return { x, y };
  }, []);

  // Expose Zoom/Pan Controls to parent component via ImperativeHandle
  useImperativeHandle(ref, () => ({
    zoomIn() {
      const oldZoom = zoomRef.current;
      const newZoom = Math.min(18, oldZoom * 1.3);
      zoomRef.current = newZoom;
      if (mapRef.current) {
        const rect = mapRef.current.getBoundingClientRect();
        const mouseX = rect.width / 2;
        const mouseY = rect.height / 2;
        const contentX = (mouseX - panRef.current.x) / oldZoom;
        const contentY = (mouseY - panRef.current.y) / oldZoom;
        panRef.current = {
          x: mouseX - contentX * newZoom,
          y: mouseY - contentY * newZoom
        };
      }
      updateTransform();
      onGlobeInteract?.();
    },
    zoomOut() {
      const oldZoom = zoomRef.current;
      const newZoom = Math.max(0.6, oldZoom / 1.3);
      zoomRef.current = newZoom;
      if (mapRef.current) {
        const rect = mapRef.current.getBoundingClientRect();
        const mouseX = rect.width / 2;
        const mouseY = rect.height / 2;
        const contentX = (mouseX - panRef.current.x) / oldZoom;
        const contentY = (mouseY - panRef.current.y) / oldZoom;
        panRef.current = {
          x: mouseX - contentX * newZoom,
          y: mouseY - contentY * newZoom
        };
      }
      updateTransform();
      onGlobeInteract?.();
    },
    resetView() {
      zoomRef.current = 1.0;
      panRef.current = { x: 0, y: 0 };
      updateTransform();
      onGlobeInteract?.();
    },
    focusCoordinates(lat: number, lng: number, nextZoom = 3.5) {
      if (mapRef.current) {
        const p = projectPoint(lng, lat);
        const rect = mapRef.current.getBoundingClientRect();
        zoomRef.current = nextZoom;
        panRef.current = {
          x: rect.width / 2 - (p.x * nextZoom),
          y: rect.height / 2 - (p.y * nextZoom)
        };
        updateTransform();
        onGlobeInteract?.();
      }
    }
  }), [updateTransform, onGlobeInteract, projectPoint]);

  // Handle active wheel zooming with { passive: false } to prevent browser console error
  useEffect(() => {
    const svg = mapRef.current;
    if (!svg) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault(); // Allowed because listener is marked passive: false
      const zoomFactor = 1.15;
      const oldZoom = zoomRef.current;
      let newZoom = e.deltaY < 0 ? oldZoom * zoomFactor : oldZoom / zoomFactor;
      newZoom = Math.max(0.6, Math.min(18, newZoom));

      const rect = svg.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const contentX = (mouseX - panRef.current.x) / oldZoom;
      const contentY = (mouseY - panRef.current.y) / oldZoom;

      panRef.current = {
        x: mouseX - contentX * newZoom,
        y: mouseY - contentY * newZoom
      };
      zoomRef.current = newZoom;
      updateTransform();
      onGlobeInteract?.();
    };

    svg.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      svg.removeEventListener('wheel', handleWheel);
    };
  }, [updateTransform, onGlobeInteract]);

  // projectPoint moved above for hoisting safety

  // Fetch GeoJSON + World Bank Population + World Bank GDP in parallel
  useEffect(() => {
    let cancelled = false;

    Promise.all([
      fetch('/ne_110m_admin_0_countries.json').then(r => r.ok ? r.json() : { features: [] }),
      fetch('https://api.worldbank.org/v2/country/all/indicator/NY.GDP.MKTP.CD?format=json&per_page=300&date=2023')
        .then(r => r.ok ? r.json() : [null, []])
        .catch(() => [null, []]),
      fetch('https://api.worldbank.org/v2/country/all/indicator/SP.POP.TOTL?format=json&per_page=300&date=2023')
        .then(r => r.ok ? r.json() : [null, []])
        .catch(() => [null, []])
    ]).then(([geoD, gdpD, popD]) => {
      if (cancelled) return;

      const features = geoD.features || [];
      setGeoJsonData(features);

      const gdpMap: Record<string, number> = {};
      if (gdpD && gdpD[1]) {
        gdpD[1].forEach((item: any) => {
          if (item && item.countryiso3code && item.value !== null) {
            gdpMap[item.countryiso3code.toUpperCase()] = item.value;
          }
        });
      }

      const popMap: Record<string, number> = {};
      if (popD && popD[1]) {
        popD[1].forEach((item: any) => {
          if (item && item.countryiso3code && item.value !== null) {
            popMap[item.countryiso3code.toUpperCase()] = item.value;
          }
        });
      }

      const parsed = features.map((f: any) => {
        const prop = f.properties;
        let lat = 0;
        let lng = 0;
        if (f.bbox && f.bbox.length === 4) {
          lng = (f.bbox[0] + f.bbox[2]) / 2;
          lat = (f.bbox[1] + f.bbox[3]) / 2;
        } else if (f.geometry && f.geometry.coordinates) {
          const coords = f.geometry.type === 'Polygon'
            ? f.geometry.coordinates[0][0]
            : f.geometry.coordinates[0][0][0];
          if (coords && coords.length === 2) {
            lng = coords[0];
            lat = coords[1];
          }
        }

        const cca2 = prop.ISO_A2 && prop.ISO_A2 !== '-99' ? String(prop.ISO_A2).trim().toUpperCase() : prop.POSTAL || '';
        const cca3 = prop.ISO_A3 && prop.ISO_A3 !== '-99' ? String(prop.ISO_A3).trim().toUpperCase() : prop.ADM0_A3 || '';
        const name = prop.NAME || prop.ADMIN || 'Unknown Region';

        const realPop = popMap[cca3] || prop.POP_EST || 1000000;
        const rawGDP = gdpMap[cca3] || (prop.GDP_MD_EST ? prop.GDP_MD_EST * 1e6 : 1e9);

        const getCapital = (c3: string, nStr: string) => {
          const capitals: Record<string, string> = {
            'USA': 'Washington, D.C.', 'CHN': 'Beijing', 'DEU': 'Berlin', 'JPN': 'Tokyo',
            'GBR': 'London', 'FRA': 'Paris', 'ITA': 'Rome', 'CAN': 'Ottawa', 'BRA': 'Brasília',
            'IND': 'New Delhi', 'RUS': 'Moscow', 'AUS': 'Canberra', 'ZAF': 'Pretoria',
            'ESP': 'Madrid', 'MEX': 'Mexico City', 'EGY': 'Cairo', 'SAU': 'Riyadh',
            'TUR': 'Ankara', 'ARG': 'Buenos Aires', 'KOR': 'Seoul', 'NGA': 'Abuja',
            'KEN': 'Nairobi', 'IDN': 'Jakarta', 'PAK': 'Islamabad', 'BGD': 'Dhaka',
            'UKR': 'Kyiv', 'POL': 'Warsaw', 'SWE': 'Stockholm', 'NOR': 'Oslo', 'FIN': 'Helsinki',
            'COL': 'Bogotá', 'PER': 'Lima', 'VEN': 'Caracas', 'CHL': 'Santiago', 'NZL': 'Wellington'
          };
          return capitals[c3] || `${nStr} Capital`;
        };

        return {
          lat,
          lng,
          name,
          cca2,
          cca3,
          capital: getCapital(cca3, name),
          population: realPop,
          region: prop.CONTINENT || prop.REGION_UN || 'Global',
          flag: cca2 && cca2 !== '-99' ? `https://flagcdn.com/${cca2.toLowerCase()}.svg` : '',
          area: prop.AREA || (prop.GDP_MD_EST ? Math.round(prop.GDP_MD_EST * 12) : 120000),
          gdp: rawGDP,
          languages: 'English, Native',
          currencies: 'Local Unit',
          timezones: 'UTC'
        };
      });

      setRawCountries(parsed);
    }).catch(() => { });

    return () => { cancelled = true; };
  }, []);

  const getCountryFromPolygon = useCallback((polygon: any) => {
    if (!polygon || !polygon.properties) return null;
    const { ISO_A3, ADM0_A3, NAME } = polygon.properties;
    const code = (ISO_A3 && ISO_A3 !== '-99' ? String(ISO_A3).trim().toUpperCase() : String(ADM0_A3).trim().toUpperCase()) || '';

    return rawCountries.find(c =>
      (code && c.cca3 === code) ||
      (c.name && NAME && c.name.toLowerCase() === String(NAME).trim().toLowerCase())
    ) || null;
  }, [rawCountries]);

  // Fetch crisis points (seismic activity)
  useEffect(() => {
    if (filterMode !== 'crises') { setNaturalCrisisPoints([]); return; }
    fetch('https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_week.geojson')
      .then(r => r.ok ? r.json() : { features: [] })
      .then(data => {
        setNaturalCrisisPoints(data.features.slice(0, 35).map((f: any) => ({
          type: 'crisis',
          id: f.id,
          title: f.properties.title,
          mag: f.properties.mag,
          lat: f.geometry.coordinates[1],
          lng: f.geometry.coordinates[0],
          size: f.properties.mag * 1.5,
          color: '#3b82f6', // Premium blue accent
        })));
      })
      .catch(() => { });
  }, [filterMode]);

  // Combined crises
  const finalCrisisPoints = useMemo(() => {
    if (filterMode !== 'crises') return [];
    return [...naturalCrisisPoints, ...ACTIVE_WARS];
  }, [naturalCrisisPoints, filterMode]);

  // Metric Helpers
  const getCountryGDP = useCallback((c: any) => {
    if (c.gdp) return parseFloat((c.gdp / 1e12).toFixed(3));
    return parseFloat(((c.population * 15000) / 1e12).toFixed(2));
  }, []);

  const getCountrySpeed = useCallback((c: any) => {
    const topSpeeds: Record<string, number> = {
      'SGP': 264, 'HKG': 258, 'CHL': 248, 'ARE': 239, 'USA': 210,
      'CHN': 185, 'JPN': 175, 'FRA': 165, 'KOR': 155, 'DEU': 140
    };
    if (topSpeeds[c.cca3]) return topSpeeds[c.cca3];
    if (c.region === 'Americas' || c.region === 'Europe') return 55 + (c.population % 80);
    return 15 + (c.population % 50);
  }, []);

  // Fetch weather stations
  useEffect(() => {
    if (filterMode !== 'weather') { setWeatherPoints([]); return; }
    const lats = WEATHER_CITIES.map(c => c.lat).join(',');
    const lngs = WEATHER_CITIES.map(c => c.lng).join(',');
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lats}&longitude=${lngs}&current_weather=true`;

    fetch(url)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) {
          const mapped = data.map((item, idx) => {
            const city = WEATHER_CITIES[idx];
            const temp = item.current_weather?.temperature ?? 15;
            const wind = item.current_weather?.windspeed ?? 10;
            const code = item.current_weather?.weathercode ?? 0;

            const getWeatherDesc = (c: number) => {
              if (c === 0) return 'Clear';
              if (c >= 1 && c <= 3) return 'Mainly Clear';
              if (c >= 45 && c <= 48) return 'Fog';
              if (c >= 51 && c <= 55) return 'Drizzle';
              if (c >= 61 && c <= 65) return 'Rain';
              if (c >= 71 && c <= 75) return 'Snow';
              if (c >= 95 && c <= 99) return 'Storm';
              return 'Cloudy';
            };

            // Blue spectrum colors instead of yellow/red warm colors
            let color = '#06b6d4';
            if (temp < 10) color = '#38bdf8';
            else if (temp > 25) color = '#3b82f6';
            else color = '#60a5fa';

            return {
              type: 'weather',
              name: city.name,
              country: city.country,
              lat: city.lat,
              lng: city.lng,
              temp,
              wind,
              desc: getWeatherDesc(code),
              color
            };
          });
          setWeatherPoints(mapped);
        }
      })
  }, [filterMode]);

  // Fetch live flights
  useEffect(() => {
    if (filterMode !== 'flights') {
      setFlightPoints([]);
      return;
    }

    const loadFlights = () => {
      fetchLiveFlights(800)
        .then(flights => {
          setFlightPoints(flights);
        })
        .catch(err => {
          console.error('Error fetching live flights in Globe:', err);
        });
    };

    // Initial load
    loadFlights();

    // Poll for updates every 30 seconds
    const interval = setInterval(loadFlights, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [filterMode]);

  // Convert GeoJSON Features to SVG paths
  const parsedPaths = useMemo(() => {
    return geoJsonData.filter(d => d.properties.ISO_A2 !== 'AQ').map((feat) => {
      const country = getCountryFromPolygon(feat);
      let pathData = '';
      const geom = feat.geometry;
      if (geom) {
        const { type, coordinates } = geom;
        if (type === 'Polygon') {
          pathData = coordinates.map((ring: any) =>
            `M ${ring.map((coord: number[]) => {
              const p = projectPoint(coord[0], coord[1]);
              return `${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
            }).join(' L ')} Z`
          ).join(' ');
        } else if (type === 'MultiPolygon') {
          pathData = coordinates.map((poly: any) =>
            poly.map((ring: any) =>
              `M ${ring.map((coord: number[]) => {
                const p = projectPoint(coord[0], coord[1]);
                return `${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
              }).join(' L ')} Z`
            ).join(' ')
          ).join(' ');
        }
      }

      return {
        feature: feat,
        path: pathData,
        country
      };
    });
  }, [geoJsonData, getCountryFromPolygon, projectPoint]);

  // Dynamic Spectrum Fill Calculator (Zinc/Slate theme matching #1c1c1c)
  const getFeatureFill = useCallback((country: any) => {
    if (!country) return 'rgba(28, 28, 28, 0.4)';

    if (filterMode === 'population') {
      const logPop = Math.log10(country.population || 1e6);
      // Soft green to emerald-teal gradient spectrum
      const h = 140 + (logPop - 6) * 12;
      return `hsla(${h}, 50%, 35%, 0.4)`;
    }

    if (filterMode === 'gdp') {
      const gdp = getCountryGDP(country);
      const intensity = Math.min(1, gdp / 25);
      // Sapphire blue to electric sky-blue gradient spectrum
      return `rgba(59, 130, 246, ${0.1 + intensity * 0.5})`;
    }

    if (filterMode === 'network') {
      const speed = getCountrySpeed(country);
      const intensity = Math.min(1, speed / 250);
      // Charcoal to light cyan spectrum
      return `rgba(6, 182, 212, ${0.1 + intensity * 0.5})`;
    }

    if (filterMode === 'heatmap') {
      const gdpPerPop = country.gdp / Math.max(1, country.population);
      const logVal = Math.max(0, Math.min(1, Math.log10(gdpPerPop + 1) / 5.5));
      // Deep violet to soft magenta spectrum
      const h = 265 - logVal * 70;
      return `hsla(${h}, 60%, 40%, 0.4)`;
    }

    return 'rgba(28, 28, 28, 0.4)';
  }, [filterMode, getCountryGDP, getCountrySpeed, selectedCountryName]);

  // Pan / Drag Mouse Listeners (Updates ref and DOM style directly for 60fps smoothness)
  const handleSvgClick = (e: React.MouseEvent) => {
    if (draggedRef.current) return;
    const target = e.target as SVGElement;
    if (target.tagName === 'svg' || target.tagName === 'rect') {
      onCountrySelect?.(null);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    draggedRef.current = false;
    mouseStartPos.current = { x: e.clientX, y: e.clientY };
    dragStart.current = { x: e.clientX - panRef.current.x, y: e.clientY - panRef.current.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const dx = e.clientX - mouseStartPos.current.x;
      const dy = e.clientY - mouseStartPos.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > 5) {
        draggedRef.current = true;
      }
      panRef.current = {
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      };
      updateTransform();
      onGlobeInteract?.();
    }

    // Direct DOM placement of tooltip to prevent React state lag
    if (tooltipRef.current && tooltipRef.current.style.display === 'block') {
      const containerRect = mapRef.current?.getBoundingClientRect();
      if (containerRect) {
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;
        tooltipRef.current.style.left = `${x + 15}px`;
        tooltipRef.current.style.top = `${y + 15}px`;
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Dynamic Touch Panning and Pinch-to-Zoom handling with passive: false to prevent browser gesture zoom
  useEffect(() => {
    const svg = mapRef.current;
    if (!svg) return;

    let touchStartDist = 0;
    let touchStartZoom = 1.0;
    let isTouchDragging = false;
    let touchStartPos = { x: 0, y: 0 };
    let touchStartPan = { x: 0, y: 0 };

    const handleTouchStart = (e: TouchEvent) => {
      // Prevent page-level scroll/zoom
      if (e.cancelable) e.preventDefault();

      if (e.touches.length === 1) {
        isTouchDragging = true;
        draggedRef.current = false;
        touchStartPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        touchStartPan = { x: panRef.current.x, y: panRef.current.y };
        onGlobeInteract?.();
      } else if (e.touches.length === 2) {
        isTouchDragging = false;
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        touchStartDist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
        touchStartZoom = zoomRef.current;
        onGlobeInteract?.();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();

      if (e.touches.length === 1 && isTouchDragging) {
        const dx = e.touches[0].clientX - touchStartPos.x;
        const dy = e.touches[0].clientY - touchStartPos.y;
        if (Math.sqrt(dx * dx + dy * dy) > 5) {
          draggedRef.current = true;
        }
        panRef.current = {
          x: touchStartPan.x + dx,
          y: touchStartPan.y + dy
        };
        updateTransform();
        onGlobeInteract?.();
      } else if (e.touches.length === 2 && touchStartDist > 0) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
        const factor = dist / touchStartDist;
        let newZoom = touchStartZoom * factor;
        newZoom = Math.max(0.6, Math.min(18, newZoom));

        const rect = svg.getBoundingClientRect();
        const midX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
        const midY = (touch1.clientY + touch2.clientY) / 2 - rect.top;

        const oldZoom = zoomRef.current;
        const contentX = (midX - panRef.current.x) / oldZoom;
        const contentY = (midY - panRef.current.y) / oldZoom;

        panRef.current = {
          x: midX - contentX * newZoom,
          y: midY - contentY * newZoom
        };
        zoomRef.current = newZoom;
        updateTransform();
        onGlobeInteract?.();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!draggedRef.current && e.changedTouches.length > 0) {
        const target = e.target as HTMLElement;
        if (target) {
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: e.changedTouches[0].clientX,
            clientY: e.changedTouches[0].clientY
          });
          target.dispatchEvent(clickEvent);
        }
      }
      isTouchDragging = false;
      touchStartDist = 0;
    };

    svg.addEventListener('touchstart', handleTouchStart, { passive: false });
    svg.addEventListener('touchmove', handleTouchMove, { passive: false });
    svg.addEventListener('touchend', handleTouchEnd, { passive: false });
    svg.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      svg.removeEventListener('touchstart', handleTouchStart);
      svg.removeEventListener('touchmove', handleTouchMove);
      svg.removeEventListener('touchend', handleTouchEnd);
      svg.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [updateTransform, onGlobeInteract]);

  // Center view on country selection prop change
  useEffect(() => {
    if (selectedCountryName && rawCountries.length > 0 && mapRef.current) {
      const target = rawCountries.find(c => c.name === selectedCountryName);
      if (target) {
        const p = projectPoint(target.lng, target.lat);
        const rect = mapRef.current.getBoundingClientRect();
        const nextZoom = 2.5;
        zoomRef.current = nextZoom;
        panRef.current = {
          x: rect.width / 2 - (p.x * nextZoom),
          y: rect.height / 2 - (p.y * nextZoom)
        };
        updateTransform();
      }
    }
  }, [selectedCountryName, rawCountries, projectPoint, updateTransform]);

  // Center on user location ping
  useEffect(() => {
    if (userLocation && mapRef.current) {
      const p = projectPoint(userLocation.lng, userLocation.lat);
      const rect = mapRef.current.getBoundingClientRect();
      const nextZoom = 3.2;
      zoomRef.current = nextZoom;
      panRef.current = {
        x: rect.width / 2 - (p.x * nextZoom),
        y: rect.height / 2 - (p.y * nextZoom)
      };
      updateTransform();
    }
  }, [userLocation, projectPoint, updateTransform]);

  const handleFeatureClick = (country: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (draggedRef.current) return;
    if (country) {
      onCountrySelect?.(country);
    }
  };

  // Helper to compute center coordinates of a state polygon
  const getStateCenter = (feat: any) => {
    let latSum = 0;
    let lonSum = 0;
    let count = 0;
    const coords = feat.geometry?.coordinates;
    if (!coords) return null;

    const processRing = (ring: number[][]) => {
      ring.forEach(c => {
        lonSum += c[0];
        latSum += c[1];
        count++;
      });
    };

    if (feat.geometry.type === 'Polygon') {
      coords.forEach(processRing);
    } else if (feat.geometry.type === 'MultiPolygon') {
      coords.forEach((poly: any) => poly.forEach(processRing));
    }

    if (count > 0) {
      return { lat: latSum / count, lon: lonSum / count };
    }
    return null;
  };

  // Setup tooltip content
  const showTooltip = useCallback((d: any, isCountry = false, isState = false) => {
    if (!d || !tooltipRef.current) {
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
      return;
    }

    let content = '';
    if (isState) {
      content = `
        <div class="font-mono text-[10px]">
          <b class="text-blue-400 text-xs">${d.name.toUpperCase()}</b><br/>
          <span class="opacity-60">State of:</span> ${d.parentCountry}
        </div>
      `;
    } else if (isCountry) {
      const gdpText = filterMode === 'gdp' || filterMode === 'heatmap' ? `<br/><span style="color:#60a5fa">GDP: $${getCountryGDP(d)}T</span>` : '';
      const netText = filterMode === 'network' ? `<br/><span style="color:#22d3ee">Speed: ${getCountrySpeed(d)} Mbps</span>` : '';
      content = `
        <div class="font-mono text-[10px]">
          <b class="text-blue-400 text-xs">${d.name.toUpperCase()}</b><br/>
          <span class="opacity-60">Capital:</span> ${d.capital}<br/>
          <span class="opacity-60">Pop:</span> ${new Intl.NumberFormat().format(d.population)}${gdpText}${netText}
        </div>
      `;
    } else if (d.type === 'crisis') {
      content = `
        <div class="font-mono text-[10px] bg-transparent">
          <b class="text-blue-400">⚠️ EARTHQUAKE INCIDENT</b><br/>
          <span>${d.title}</span><br/>
          <span class="text-blue-300">Magnitude: ${d.mag}</span>
        </div>
      `;
    } else if (d.type === 'war') {
      content = `
        <div class="font-mono text-[10px] bg-transparent">
          <b class="text-red-500">⚔️ ACTIVE CONFLICT</b><br/>
          <span class="text-white font-semibold">${d.title}</span><br/>
          <span class="opacity-60">Location:</span> ${d.country}<br/>
          <span class="text-cyan-400">Status: ${d.intensity}</span>
        </div>
      `;
    } else if (d.type === 'weather') {
      content = `
        <div class="font-mono text-[10px]">
          <b class="text-cyan-400">🌦️ WEATHER INDEX</b><br/>
          <span>${d.name}, ${d.country}</span><br/>
          <span class="text-blue-300">Temp: ${d.temp}°C</span><br/>
          <span class="opacity-60">Wind: ${d.wind} km/h</span> | <span class="text-cyan-300">${d.desc}</span>
        </div>
      `;
    } else if (d.type === 'flight') {
      content = `
        <div class="font-mono text-[10px] bg-transparent">
          <b class="text-cyan-400">✈️ LIVE FLIGHT STREAM</b><br/>
          <span class="text-white font-semibold">Callsign: ${d.callsign || 'N/A'}</span><br/>
          <span class="opacity-60">Country:</span> ${d.country}<br/>
          <span class="opacity-60">Alt:</span> ${d.altitude}<br/>
          <span class="opacity-60">Speed:</span> ${d.velocity}<br/>
          <span class="opacity-60">Heading:</span> ${d.heading}<br/>
          <span class="opacity-40 text-[8px] mt-1 block">ICAO24: ${d.icao24}</span>
        </div>
      `;
    }

    tooltipRef.current.innerHTML = content;
    tooltipRef.current.style.display = 'block';
  }, [filterMode, getCountryGDP, getCountrySpeed]);

  // Color scale spectrum configs (Top-Right HUD widget)
  const legendConfig = useMemo(() => {
    switch (filterMode) {
      case 'population':
        return {
          title: 'POPULATION INDEX',
          gradient: 'linear-gradient(to right, hsla(140, 50%, 35%, 0.2), hsla(190, 70%, 40%, 0.8))',
          low: '1M',
          high: '1.4B'
        };
      case 'gdp':
        return {
          title: 'ECONOMIC GDP',
          gradient: 'linear-gradient(to right, rgba(30, 58, 138, 0.2), rgba(56, 189, 248, 0.8))',
          low: '$1B',
          high: '$27T'
        };
      case 'network':
        return {
          title: 'NETWORK BANDWIDTH',
          gradient: 'linear-gradient(to right, rgba(39, 39, 42, 0.2), rgba(6, 182, 212, 0.8))',
          low: '10 Mbps',
          high: '260 Mbps'
        };
      case 'heatmap':
        return {
          title: 'WEALTH DENSITY (GDP/CAP)',
          gradient: 'linear-gradient(to right, hsla(265, 60%, 40%, 0.2), hsla(195, 60%, 40%, 0.8))',
          low: 'LOW',
          high: 'HIGH'
        };
      default:
        return null;
    }
  }, [filterMode]);

  return (
    <div
      className="absolute inset-0 bg-[#121212] overflow-hidden select-none"
      style={{ willChange: 'transform', transform: 'translate3d(0,0,0)' }}
    >
      <style>{`
        .country-path {
          transition: fill 150ms ease, stroke 150ms ease, stroke-width 150ms ease;
          vector-effect: non-scaling-stroke;
        }
        .country-path:hover {
          stroke: #60a5fa !important;
          stroke-width: 0.8px !important;
        }
        .state-path {
          transition: fill 150ms ease, stroke 150ms ease, stroke-width 150ms ease;
          vector-effect: non-scaling-stroke;
        }
        .state-path:hover {
          stroke: #60a5fa !important;
          stroke-width: 0.3px !important;
        }
        @keyframes country-shimmer {
          0% { fill-opacity: 0.15; stroke-opacity: 0.25; }
          50% { fill-opacity: 0.45; stroke-opacity: 0.65; }
          100% { fill-opacity: 0.15; stroke-opacity: 0.25; }
        }
        .country-shimmering {
          animation: country-shimmer 3s infinite ease-in-out;
          fill: rgba(6, 182, 212, 0.25) !important;
          stroke: rgba(6, 182, 212, 0.6) !important;
        }
      `}</style>

      {/* Grid Pattern Background - matching clean slate look */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#242427_1px,transparent_1px),linear-gradient(to_bottom,#242427_1px,transparent_1px)] bg-[size:40px_40px] opacity-15 pointer-events-none" />

      {/* Main Map SVG Canvas */}
      <svg
        ref={mapRef}
        className="w-full h-full cursor-grab active:cursor-grabbing pointer-events-auto"
        style={{ willChange: 'transform', transform: 'translate3d(0,0,0)' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleSvgClick}
        viewBox="0 0 1000 500"
        preserveAspectRatio="xMidYMid slice"
      >
        <g ref={mapGroupRef} className="transition-transform duration-75 ease-out" style={{ transformOrigin: '0 0' }}>

          <rect x="0" y="0" width="1000" height="500" fill="transparent" stroke="rgba(255,255,255,0.02)" strokeWidth="1" />

          {/* Subtle Grid reference markers */}
          <line x1="0" y1="250" x2="1000" y2="250" stroke="rgba(255, 255, 255, 0.04)" strokeDasharray="2 4" />
          <line x1="500" y1="0" x2="500" y2="500" stroke="rgba(255, 255, 255, 0.04)" strokeDasharray="2 4" />

          {/* Render Country Paths */}
          {parsedPaths.map(({ feature, path, country }, idx) => {
            const fill = getFeatureFill(country);
            const isShimmering = loadingStates && selectedCountryName && country && country.name === selectedCountryName;

            return (
              <path
                key={idx}
                d={path}
                fill={fill}
                stroke="#52525b" // Zinc-600 border (more visible)
                strokeWidth={0.5} // More visible border line width
                vectorEffect="non-scaling-stroke"
                className={`country-path cursor-pointer ${isShimmering ? 'country-shimmering' : ''}`}
                onMouseEnter={() => {
                  if (country) showTooltip(country, true);
                }}
                onMouseLeave={() => {
                  showTooltip(null);
                }}
                onClick={(e) => handleFeatureClick(country, e)}
              />
            );
          })}

          {/* Render State Paths for focused country */}
          {stateGeoJson && stateGeoJson.features && stateGeoJson.features.map((feat: any, idx: number) => {
            let pathData = '';
            const geom = feat.geometry;
            if (geom) {
              const { type, coordinates } = geom;
              if (type === 'Polygon') {
                pathData = coordinates.map((ring: any) =>
                  `M ${ring.map((coord: number[]) => {
                    const p = projectPoint(coord[0], coord[1]);
                    return `${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
                  }).join(' L ')} Z`
                ).join(' ');
              } else if (type === 'MultiPolygon') {
                pathData = coordinates.map((poly: any) =>
                  poly.map((ring: any) =>
                    `M ${ring.map((coord: number[]) => {
                      const p = projectPoint(coord[0], coord[1]);
                      return `${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
                    }).join(' L ')} Z`
                  ).join(' ')
                ).join(' ');
              }
            }

            const stateName = feat.properties?.shapeName || feat.properties?.NAME || feat.properties?.name || `State ${idx + 1}`;
            const center = getStateCenter(feat);
            const pCenter = center ? projectPoint(center.lon, center.lat) : null;

            return (
              <g key={`state-group-${idx}`}>
                <path
                  d={pathData}
                  fill="rgba(59, 130, 246, 0.15)"
                  stroke="rgba(96, 165, 250, 0.5)"
                  strokeWidth="0.25"
                  vectorEffect="non-scaling-stroke"
                  className="state-path cursor-pointer hover:fill-blue-500/25 hover:stroke-blue-400"
                  onMouseEnter={() => {
                    showTooltip({ name: stateName, parentCountry: selectedCountryName }, false, true);
                  }}
                  onMouseLeave={() => {
                    showTooltip(null);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (draggedRef.current) return;
                    onStateSelect?.({
                      name: stateName,
                      country: selectedCountryName,
                      lat: center?.lat,
                      lng: center?.lon
                    });
                  }}
                />
                {showStateNames && pCenter && (() => {
                  const words = stateName.split(' ');
                  return (
                    <text
                      x={pCenter.x}
                      y={pCenter.y - (words.length - 1) * 0.4} // Center vertically
                      fill="rgba(255, 255, 255, 0.55)"
                      fontSize="0.85"
                      fontWeight="500"
                      fontFamily="monospace"
                      textAnchor="middle"
                      pointerEvents="none"
                      className="select-none pointer-events-none uppercase tracking-normal"
                      style={{ fontSize: '0.85px', fill: 'rgba(255, 255, 255, 0.55)' }}
                    >
                      {words.map((word, wIdx) => (
                        <tspan
                          key={wIdx}
                          x={pCenter.x}
                          dy={wIdx === 0 ? 0 : 0.9} // Offset line height
                        >
                          {word}
                        </tspan>
                      ))}
                    </text>
                  );
                })()}
              </g>
            );
          })}

          {/* Render Continent Labels (Names only, no borders) */}
          <g className="continents-group" style={{ display: 'inline' }}>
            {CONTINENT_LABELS.map((c, idx) => {
              const p = projectPoint(c.lng, c.lat);
              return (
                <text
                  key={`continent-${idx}`}
                  x={p.x}
                  y={p.y}
                  fill="rgba(255, 255, 255, 0.28)"
                  fontSize="8"
                  fontWeight="600"
                  fontFamily="monospace"
                  textAnchor="middle"
                  letterSpacing="1.2"
                  pointerEvents="none"
                  className="select-none uppercase tracking-wider"
                >
                  {c.name}
                </text>
              );
            })}
          </g>

          {/* Clean paths representation for flights without scanning rings */}
          {filterMode === 'flights' && (
            <>
              <g pointerEvents="none">
                {FLIGHT_HUBS.map((hub, idx) => {
                  const p = projectPoint(hub.lng, hub.lat);
                  const nextHub = FLIGHT_HUBS[(idx + 4) % FLIGHT_HUBS.length];
                  const p2 = projectPoint(nextHub.lng, nextHub.lat);
                  return (
                    <g key={idx}>
                      <line
                        x1={p.x}
                        y1={p.y}
                        x2={p2.x}
                        y2={p2.y}
                        stroke="rgba(96, 165, 250, 0.12)"
                        strokeWidth="0.5"
                        vectorEffect="non-scaling-stroke"
                      />
                      <circle cx={p.x} cy={p.y} r="1.0" fill="#3b82f6" vectorEffect="non-scaling-stroke" />
                    </g>
                  );
                })}
              </g>

              {/* Real-time OpenSky Flights overlay */}
              {flightPoints.map((f, i) => {
                const p = projectPoint(f.lng, f.lat);
                return (
                  <g key={f.icao24 + '-' + i} className="cursor-pointer">
                    <g
                      transform={`translate(${p.x}, ${p.y}) rotate(${f.heading}) scale(0.45)`}
                      onMouseEnter={() => showTooltip({
                        type: 'flight',
                        icao24: f.icao24,
                        callsign: f.callsign || 'N/A',
                        country: f.country,
                        altitude: `${Math.round(f.altitude)} m (${Math.round(f.altitude * 3.28084)} ft)`,
                        velocity: `${Math.round(f.velocity * 3.6)} km/h (${Math.round(f.velocity * 1.94384)} kts)`,
                        heading: `${Math.round(f.heading)}°`
                      }, false)}
                      onMouseLeave={() => showTooltip(null)}
                    >
                      {/* Interactive ping circle removed */}
                      {/* Premium Airplane icon pointing straight UP (North) at 0 deg */}
                      <path
                        d="
      M32 2
      C35 2 37 5 37 9
      L37 22

      L56 29
      C58 30 58 34 56 35
      L37 32

      L37 50
      L44 56
      L44 59
      L32 55
      L20 59
      L20 56
      L27 50

      L27 32
      L8 35
      C6 34 6 30 8 29
      L27 22

      L27 9
      C27 5 29 2 32 2
      Z
    "
                        fill="#22d3ee"
                        stroke="#1e293b"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                        transform="translate(-16, -15.25) scale(0.5)"
                      />
                    </g>
                  </g>
                );
              })}
            </>
          )}

          {/* Clean network grid links */}
          {filterMode === 'network' && (
            <g pointerEvents="none">
              {FLIGHT_HUBS.map((hub, idx) => {
                const p1 = projectPoint(hub.lng, hub.lat);
                const p2 = projectPoint(FLIGHT_HUBS[(idx + 1) % FLIGHT_HUBS.length].lng, FLIGHT_HUBS[(idx + 1) % FLIGHT_HUBS.length].lat);
                return (
                  <line
                    key={idx}
                    x1={p1.x}
                    y1={p1.y}
                    x2={p2.x}
                    y2={p2.y}
                    stroke="rgba(6, 182, 212, 0.1)"
                    strokeWidth="0.4"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
            </g>
          )}

          {/* Weather points */}
          {filterMode === 'weather' && weatherPoints.map((w, i) => {
            const p = projectPoint(w.lng, w.lat);
            return (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r="1.2"
                fill={w.color}
                opacity="0.85"
                className="cursor-pointer"
                vectorEffect="non-scaling-stroke"
                onMouseEnter={() => showTooltip(w, false)}
                onMouseLeave={() => showTooltip(null)}
                onClick={() => {
                  const matched = rawCountries.find(c => c.name.toLowerCase() === w.country.toLowerCase());
                  if (matched) onCountrySelect?.(matched);
                }}
              />
            );
          })}

          {/* Crisis points */}
          {filterMode === 'crises' && finalCrisisPoints.map((c, i) => {
            const p = projectPoint(c.lng, c.lat);
            const isWar = c.type === 'war';
            return (
              <g key={i}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={isWar ? 1.8 : 1.2}
                  fill={isWar ? "#ef4444" : "#3b82f6"}
                  opacity="0.9"
                  className="cursor-pointer"
                  vectorEffect="non-scaling-stroke"
                  onMouseEnter={() => showTooltip(c, false)}
                  onMouseLeave={() => showTooltip(null)}
                  onClick={(e) => { e.stopPropagation(); onCrisisSelect?.(c); }}
                />
              </g>
            );
          })}

          {/* User local ping - simple, clean static marker with static outline */}
          {userLocation && (
            <g pointerEvents="none">
              <circle
                cx={projectPoint(userLocation.lng, userLocation.lat).x}
                cy={projectPoint(userLocation.lng, userLocation.lat).y}
                r="2.0"
                fill="#10b981" // soft green
                stroke="#ffffff"
                strokeWidth="0.6"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={projectPoint(userLocation.lng, userLocation.lat).x}
                cy={projectPoint(userLocation.lng, userLocation.lat).y}
                r="5.0"
                fill="none"
                stroke="#10b981"
                strokeWidth="0.6"
                opacity="0.45"
                vectorEffect="non-scaling-stroke"

              />
            </g>
          )}

        </g>
      </svg>

      {/* Floating HUD Tooltip */}
      <div
        ref={tooltipRef}
        style={{ display: 'none', position: 'absolute' }}
        className="z-50 pointer-events-none bg-[#1c1c1c]/95 border border-[#2d2d2d] px-3 py-2 rounded-lg text-[10px] font-mono text-zinc-300 shadow-xl backdrop-blur-md max-w-xs transition-all duration-75"
      />



      <style>{`
        @keyframes slow-shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>

      {/* Color Scale Spectrum HUD widget (Top Right) */}
      {legendConfig && (
        <div className="absolute top-1 right-1 z-20 w-44 bg-[#1c1c1c]/90 border border-[#2d2d2d] rounded-lg p-2.5 font-mono text-[9px] text-zinc-400 shadow-md backdrop-blur-md">
          <div className="font-bold text-zinc-300 mb-1.5 tracking-wider text-[8px]">{legendConfig.title}</div>
          <div className="h-2 w-full rounded mb-1" style={{ background: legendConfig.gradient }} />
          <div className="flex justify-between text-[8px] text-zinc-500 font-semibold">
            <span>{legendConfig.low}</span>
            <span>{legendConfig.high}</span>
          </div>
        </div>
      )}

    </div>
  );
}));

JarvisGlobe.displayName = 'JarvisGlobe';
export default JarvisGlobe;
